import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Twitter, Facebook, Linkedin, Mail, Send, MessageCircle } from "lucide-react";
import { ICON_STYLES, ICON_SIZES, iconWrapperProps, invalidateIconStyleCache } from "@/lib/iconStyles";

// Small demo strip that mirrors how real icons render on the site.
function StylePreview({ styleName, sizeName }) {
  const demo = ["twitter", "facebook", "linkedin", "whatsapp"];
  return (
    <div className="flex items-center gap-2">
      {demo.map(key => {
        const { className, style, iconSizeClass } = iconWrapperProps({
          platformKey: key, styleName, sizeName,
        });
        const Icon = ({ twitter: Twitter, facebook: Facebook, linkedin: Linkedin, whatsapp: MessageCircle }[key]);
        return (
          <span key={key} className={className} style={style} aria-hidden="true">
            <Icon className={iconSizeClass}/>
          </span>
        );
      })}
      <Mail className="w-3 h-3 text-slate-300 ml-2 sr-only"/>
      <Send className="w-3 h-3 text-slate-300 sr-only"/>
    </div>
  );
}

/**
 * Standalone card that lets an admin pick icon style + size. This is a global
 * setting driving both the blog Share bar and the footer social icons, but it's
 * intentionally embedded in BOTH panels ("Blog display" and "Footer & Socials")
 * so admins encounter it in-context. Both instances read/write the same key.
 */
export default function IconStylePicker() {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/site-settings/icon-style").then(r => setState(r.data));
  useEffect(() => { load(); }, []);

  const persist = async (next) => {
    setState(next);
    setSaving(true);
    try {
      await api.put("/site-settings/icon-style", next);
      invalidateIconStyleCache();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      load();
    } finally { setSaving(false); }
  };

  if (!state) return <div className="text-sm text-slate-500 py-4">Loading icon style…</div>;

  return (
    <div className="border border-slate-200 rounded-lg p-5 bg-white">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="font-heading font-semibold text-slate-900 text-sm">Icon style &amp; size</div>
          <p className="text-xs text-slate-500 mt-0.5">
            Applies to both the blog <strong>Share bar</strong> and the site <strong>footer socials</strong>.
          </p>
        </div>
        <span className="text-xs uppercase tracking-widest text-slate-400">{saving ? "Saving…" : "Auto-saves"}</span>
      </div>

      {/* Style grid */}
      <div className="grid sm:grid-cols-2 gap-2">
        {ICON_STYLES.map(s => {
          const active = state.style === s.value;
          return (
            <button key={s.value}
                    data-testid={`icon-style-${s.value}`}
                    onClick={() => persist({ ...state, style: s.value })}
                    className={`text-left rounded-lg border p-3 transition-colors ${
                      active
                        ? "border-sky-600 bg-sky-50 ring-1 ring-sky-600"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-heading font-semibold text-slate-900 text-sm">{s.label}</div>
                {active && <span className="text-[10px] uppercase tracking-widest text-sky-700 font-semibold">Selected</span>}
              </div>
              <div className="text-xs text-slate-500 mb-3">{s.desc}</div>
              <StylePreview styleName={s.value} sizeName={state.size}/>
            </button>
          );
        })}
      </div>

      {/* Size chips */}
      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <span className="text-xs uppercase tracking-widest text-slate-500 mr-2">Icon size</span>
        {ICON_SIZES.map(sz => {
          const active = state.size === sz.value;
          return (
            <button key={sz.value}
                    data-testid={`icon-size-${sz.value}`}
                    onClick={() => persist({ ...state, size: sz.value })}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      active
                        ? "bg-sky-700 text-white"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}>
              {sz.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
